/**
 * `<InviteTeammateDialog>` — invite a teammate to complete a technical step
 * (DB creds, API spec) from inside the onboarding wizard (WP-8).
 *
 * Reuses the existing members-invite mechanism (`authClient.inviteMember`, the
 * same endpoint `<InviteMemberDialog>` calls) with role `member`. We do NOT
 * build a new invite path — the invitee accepts via the existing `/join` flow
 * and the surface gate routes them straight to `/onboard`.
 *
 * On success: invalidate the onboarding overview so the inviter's "waiting on
 * X" chip (pending_invitations) appears, toast, close, reset.
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../../lib/format-error";
import { onboardingKeys } from "../api/onboarding";

export function InviteTeammateDialog({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("onboarding");
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (open) setEmail("");
  }, [open]);

  const mutation = useMutation({
    mutationFn: (value: string) => authClient.inviteMember(slug, { email: value, role: "member" }),
    onSuccess: (_inv, value) => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.overview(slug) });
      toast.success(t("invite.toast", { email: value }));
      onOpenChange(false);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("invite.failed"), { description: formatApiError(err) });
    },
  });

  const trimmed = email.trim();
  const canSubmit = trimmed !== "" && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent data-testid="onboarding-invite-form">
        <DialogHeader>
          <DialogTitle>{t("invite.title")}</DialogTitle>
          <DialogDescription>{t("invite.description")}</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate(trimmed);
          }}
        >
          <Label htmlFor="onboarding-invite-email">{t("invite.emailLabel")}</Label>
          <Input
            id="onboarding-invite-email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            placeholder={t("invite.emailPlaceholder")}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="onboarding-invite-email"
          />
          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit} data-testid="onboarding-invite-submit">
              {mutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("invite.submitting")}
                </>
              ) : (
                t("invite.submit")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
